import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Eye, Users, TrendingUp, Calendar, MapPin, Clock, Building2 } from 'lucide-react';
import { translateRegion } from '../../utils/regionTranslate';

function LabVisitorStats({ user }) {
    const [loading, setLoading] = useState(true);
    const [totalListVisits, setTotalListVisits] = useState(0);
    const [myProfileVisits, setMyProfileVisits] = useState(0);
    const [todayMyVisits, setTodayMyVisits] = useState(0);
    const [weekMyVisits, setWeekMyVisits] = useState(0);
    const [recentVisits, setRecentVisits] = useState([]);
    const [visitsByRegion, setVisitsByRegion] = useState([]);
    const [isMobile] = useState(window.innerWidth < 768);

    useEffect(() => { if (user?.uid) loadStats(); }, [user]);

    const loadStats = async () => {
        try {
            // 보안(2026-10-11): 컬렉션 전체 읽기 금지 — rules가 labId 기준으로만 읽기 허용하므로 쿼리를 둘로 분리
            //   기록 측(PublicLabDirectory·LabProfile)은 labId와 page를 항상 짝으로 기록하므로
            //   labId 필터 = 기존 page/labId OR 필터와 같은 결과
            // 10-11: find-lab 기록은 관리자만 읽기(과거 IP·위치 잔존) — 기공소 계정은 거부되므로 실패해도 내 통계는 계속 보인다
            const [listRes, mySnap] = await Promise.all([
                // 전체 기공소 찾기 페이지 방문(관리자만 성공)
                getDocs(query(collection(db, 'qrVisits'), where('labId', '==', 'find-lab'))).then(s => s, e => { console.warn('[방문 통계] 기공소 찾기 방문 수 조회 불가:', e?.code || e?.message); return null; }),
                // 내 기공소 프로필 방문
                getDocs(query(collection(db, 'qrVisits'), where('labId', '==', user.uid))),
            ]);
            setTotalListVisits(listRes ? listRes.size : null); // null = 볼 수 없음('-' 표시, 0과 구분)

            const myVisits = mySnap.docs.map(d => ({ id: d.id, ...d.data() }));
            setMyProfileVisits(myVisits.length);

            const today = new Date(); today.setHours(0, 0, 0, 0);
            const todayList = myVisits.filter(v => {
                const t = v.visitedAt?.toDate?.() || new Date(v.visitedAt || 0);
                return t >= today;
            });
            setTodayMyVisits(todayList.length);

            const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
            const weekList = myVisits.filter(v => {
                const t = v.visitedAt?.toDate?.() || new Date(v.visitedAt || 0);
                return t >= weekAgo;
            });
            setWeekMyVisits(weekList.length);

            const sorted = myVisits.sort((a, b) => {
                const ta = a.visitedAt?.toDate?.() || new Date(a.visitedAt || 0);
                const tb = b.visitedAt?.toDate?.() || new Date(b.visitedAt || 0);
                return tb - ta;
            }).slice(0, 30);
            setRecentVisits(sorted);

            const regionMap = {};
            myVisits.forEach(v => {
                const r = v.city ? translateRegion(`${v.region || ''} ${v.city}`.trim()) : '';
                if (r) regionMap[r] = (regionMap[r] || 0) + 1;
            });
            setVisitsByRegion(Object.entries(regionMap).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count));
        } catch (e) { console.error('방문 통계 로드 실패:', e); }
        finally { setLoading(false); }
    };

    const formatDate = (v) => {
        try {
            const d = v?.toDate?.() || new Date(v);
            return d.toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        } catch { return '-'; }
    };

    const card = { backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20, marginBottom: 16 };
    const statCard = { ...card, textAlign: 'center', flex: 1 };

    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>불러오는 중...</div>;

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: isMobile ? 16 : 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <Building2 size={24} color="#f59e0b" />
                <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>기공소 홍보 유입 통계</h1>
            </div>
            <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>기공소 찾기 페이지와 우리 기공소 프로필 방문자 분석</p>

            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexDirection: isMobile ? 'column' : 'row' }}>
                <div style={statCard}>
                    <Users size={24} color="#f59e0b" style={{ marginBottom: 8 }} />
                    <div style={{ fontSize: 28, fontWeight: 900, color: '#f59e0b' }}>{totalListVisits == null ? '-' : totalListVisits.toLocaleString()}</div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>기공소 찾기 총 유입</div>
                </div>
                <div style={statCard}>
                    <Eye size={24} color="#10b981" style={{ marginBottom: 8 }} />
                    <div style={{ fontSize: 28, fontWeight: 900, color: '#10b981' }}>{myProfileVisits.toLocaleString()}</div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>우리 기공소 조회</div>
                </div>
                <div style={statCard}>
                    <TrendingUp size={24} color="#6366f1" style={{ marginBottom: 8 }} />
                    <div style={{ fontSize: 28, fontWeight: 900, color: '#6366f1' }}>{weekMyVisits}</div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>최근 7일</div>
                </div>
                <div style={statCard}>
                    <Calendar size={24} color="#ef4444" style={{ marginBottom: 8 }} />
                    <div style={{ fontSize: 28, fontWeight: 900, color: '#ef4444' }}>{todayMyVisits}</div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>오늘</div>
                </div>
            </div>

            <div style={{ ...card, backgroundColor: '#fffbeb', border: '1px solid #fde68a', textAlign: 'center' }}>
                <div style={{ fontSize: 13, color: '#92400e', marginBottom: 4 }}>기공소 찾기 → 우리 기공소 전환율</div>
                <div style={{ fontSize: 32, fontWeight: 900, color: '#92400e' }}>
                    {totalListVisits > 0 ? `${((myProfileVisits / totalListVisits) * 100).toFixed(1)}%` : '-'}
                </div>
            </div>

            <div style={card}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Clock size={18} color="#10b981" /> 최근 방문
                </h3>
                {recentVisits.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: 14 }}>아직 방문 기록이 없습니다</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {recentVisits.slice(0, 10).map((v, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <span style={{ color: '#334155' }}>방문 #{recentVisits.length - i}</span>
                                <span style={{ color: '#94a3b8' }}>{formatDate(v.visitedAt)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default LabVisitorStats;
